import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';
import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { NavRoute } from '@databox/ember-integrations/lib/components/Navigation/navigation.contract';

import type { Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

type Args = Record<string, never>;

interface SectionLink {
	path: string;
	text: string;
	selected?: boolean;
	isNew?: boolean; // show green badge with 'New' text
	isLocked?: boolean;
	isBeta?: boolean;
	isSoon?: boolean;
	noUppercase?: boolean; // prevent uppercase text transform
}

const DATA_PAGE_TITLES: Partial<Record<NavRoute, string>> = {
	[NavRoute.DATA_SOURCES]: 'Data sources',
	[NavRoute.DATA_DATASETS]: 'Datasets',
	[NavRoute.DATA_MERGED_DATASETS]: 'Merged datasets',
	[NavRoute.DATA_CONNECTIONS]: 'Connections',
	[NavRoute.DATA_INTEGRATIONS]: 'Integrations',
	[NavRoute.DATA_METRIC_LIBRARY]: 'Metric library',
};

export default class NavigationSectionComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('account-progress') declare accountProgressService: Service.AccountProgress;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('navigation-main') declare navigationMainService: Service.NavigationMain;
	@service('router') declare routerService: Service.Router;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	@tracked isDropdownOpen = false; // for narrow (mobile) views navigation section opens below

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const closeDropdownMenu = () => {
			if (isAlive(this)) {
				this.isDropdownOpen = false;
			}
		};

		this.routerService.on('routeWillChange', closeDropdownMenu);
		this.closeDropdownMenuFn = closeDropdownMenu;
	}

	get extraClasses(): string {
		const extraClasses = [];

		if (this.selectedLink) {
			extraClasses.push('has-subsection');
		} else {
			extraClasses.push('no-subsection');
		}

		// hide title and change filter-line positions on mobile widths when filter-line-proxy is active
		if (this.navigationService.isFilterSearchActive) {
			extraClasses.push('search-active');
		}

		return extraClasses.join(' ');
	}

	get isActiveUserViewer(): boolean {
		return this.currentUserService.isViewer;
	}

	setSelectedRoute(links: SectionLink[]): SectionLink[] {
		const activatedRoute = this.navigationService.activatedRoute;

		return links.map((link) => ({ ...link, selected: activatedRoute.startsWith(link.path) }));
	}

	get sectionLinks(): SectionLink[] {
		let links: SectionLink[] = [];

		switch (this.navigationService.activatedSection) {
			case 'notifications':
				// deprecated subroute, dont show any links
				links = [];
				break;
			case 'settings':
				links = this.sectionLinksAccount;
				break;
			case 'clients':
				links = this.sectionLinksClients;
				break;
			case 'boards':
				links = this.sectionLinksBoards;
				break;
			case 'reports':
				links = this.sectionLinksReports;
				break;
			case 'goals':
				links = this.sectionLinksGoals;
				break;
			default:
				links = [];
				break;
		}

		// set locked
		links.forEach((link) => {
			if (link.path) {
				link.isLocked = this.billingService.isRouteLocked(link.path);
			}
		});

		return this.setSelectedRoute(links);
	}

	get selectedLink() {
		return this.sectionLinks.find((item) => item.selected);
	}

	get sectionLinksAccount(): SectionLink[] {
		const userCount = this.mmUsersService.usersWithoutAgencyAdminsRecords.length;
		const isAdmin = this.currentUserService.isAdmin;
		let sublinks: SectionLink[] = [];

		if (isAdmin) {
			sublinks = [
				{
					path: 'settings.users',
					text: 'Users' + (userCount ? `<small>${userCount}</small>` : ''),
				},
			];
		}

		sublinks.push({
			path: 'settings.notifications',
			text: 'Notifications',
		});

		if (isAdmin) {
			if (!this.accountService.isClient) {
				sublinks.push({ path: 'settings.billing.plans', text: 'Plans' });
			}

			sublinks.push({ path: 'settings.billing.index', text: 'Billing' });
		}

		if (this.accountProgressService.canSeeAccountProgressSubpage) {
			sublinks.push({ path: 'settings.level', text: 'Setup progress' });
		}

		if (isAdmin) {
			sublinks.push({ path: 'settings.logs', text: 'Activity' });
		}

		return sublinks;
	}

	get sectionLinksClients(): SectionLink[] {
		const sublinks = [];

		sublinks.push({ path: 'clients.accounts', text: 'Accounts overview' });
		sublinks.push({ path: 'clients.performance', text: 'Metric performance overview' });
		sublinks.push({ path: 'clients.goals', text: 'Goals overview' });

		return sublinks;
	}

	get sectionLinksBoards(): SectionLink[] {
		const sublinks: SectionLink[] = [];

		if (this.isActiveUserViewer) {
			return sublinks;
		}

		sublinks.push({ path: 'boards.index', text: 'Dashboards' });
		sublinks.push({ path: 'boards.loops', text: 'Looped dashboards' });
		sublinks.push({ path: 'boards.account-templates', text: 'Account templates' });

		if (this.accountService.showPublicTemplates) {
			sublinks.push({ path: 'boards.public-templates', text: 'Public templates' });
		}

		return sublinks;
	}

	get sectionLinksReports(): SectionLink[] {
		const sublinks = [];

		sublinks.push({ path: 'reports.index', text: 'Reports' });
		if (this.navigationService.initialRequestsDone && this.billingService.sharingEnabled) {
			sublinks.push({ path: 'reports.schedules', text: 'Scheduled' });
		}
		sublinks.push({ path: 'reports.archive', text: 'Archive' });
		sublinks.push({ path: 'reports.account-templates', text: 'Account templates' });

		if (this.accountService.showPublicTemplates) {
			sublinks.push({ path: 'reports.public-templates', text: 'Public templates' });
		}

		return sublinks;
	}

	get sectionLinksGoals(): SectionLink[] {
		const sublinks: SectionLink[] = [];

		sublinks.push({ path: 'goals.my', text: 'My goals' });
		sublinks.push({ path: 'goals.team', text: 'Team goals' });
		sublinks.push({ path: 'goals.company', text: 'Company goals' });
		sublinks.push({
			path: 'goals.strategy',
			text: 'OKRs',
			isBeta: this.mmGoalsService.isGoalsOKRsEnabled,
			isLocked: !this.mmGoalsService.isGoalsOKRsEnabled,
			noUppercase: true,
		});

		return sublinks;
	}

	get hideH1(): boolean {
		return this.navigationService.isWizard;
	}

	get sectionTitle(): string {
		const navRoute = this.navigationMainService.activatedNavRoute;

		return (navRoute && DATA_PAGE_TITLES[navRoute]) || this.navigationService.sectionTitle;
	}

	get sectionIsNew() {
		return this.navigationService.sectionIsNew;
	}

	get isButtonsContainerVisible(): boolean {
		return (
			!!this.navigationService.addButtonContainerNode ||
			!!this.navigationService.batchButtonContainerNode ||
			!!this.navigationService.generateWithAIButtonContainerNode
		);
	}

	@action
	toggleMenu(): void {
		this.isDropdownOpen = !this.isDropdownOpen;
	}

	@action
	addAddButtonContainerReference(node: HTMLElement) {
		this.navigationService.addButtonContainerNode = node;
	}

	@action
	removeAddButtonContainerReference() {
		this.navigationService.addButtonContainerNode = null;
	}

	@action
	addBatchButtonContainerReference(node: HTMLElement) {
		this.navigationService.batchButtonContainerNode = node;
	}

	@action
	removeBatchButtonContainerReference() {
		this.navigationService.batchButtonContainerNode = null;
	}

	@action
	addGenerativeAIButtonContainerReference(node: HTMLElement) {
		this.navigationService.generateWithAIButtonContainerNode = node;
	}

	@action
	removeGenerativeAIButtonContainerReference() {
		this.navigationService.generateWithAIButtonContainerNode = null;
	}

	override willDestroy(): void {
		super.willDestroy();

		this.routerService.off('routeWillChange', this.closeDropdownMenuFn);
	}

	private closeDropdownMenuFn: (transition: PublicTransition) => void;
	<template>
		<div id="navigation-section" class="smart-center-pos {{this.extraClasses}}">
			<div class="container">

				{{#unless this.hideH1}}
					<h1 class={{if this.sectionIsNew "with-tag"}}>
						{{this.sectionTitle}}
						{{#if this.sectionIsNew}}
							<div class="new-feature-tag">New</div>
						{{/if}}
					</h1>
				{{/unless}}

				{{#if this.sectionLinks}}
					{{#if this.selectedLink}}
						<a role="button" class="single-selected" {{on "click" this.toggleMenu}}>
							{{{this.selectedLink.text}}}
							<CommonUiIconComponent @name="ChevronDown" @size="md" />
						</a>
					{{/if}}
					<nav class="section-links {{if this.isDropdownOpen 'open'}}">
						{{#each this.sectionLinks as |link|}}
							<li
								class="navigation-button
									{{if link.selected 'navigation-button--selected'}}"
							>
								<LinkTo
									@route={{link.path}}
									class="navigation-button-link
										{{if link.noUppercase 'no-uppercase'}}"
								>
									{{{link.text}}}
									{{#if link.isLocked}}
										<div class="lock-feature-tag">
											<CommonUiIconComponent @name="Lock" @size="sm" />
										</div>
									{{/if}}
									{{#if link.isNew}}
										<div class="new-feature-tag">New</div>
									{{/if}}
									{{#if link.isBeta}}
										<div class="beta-tag">Beta</div>
									{{/if}}
									{{#if link.isSoon}}
										<div class="soon-tag">Soon</div>
									{{/if}}
								</LinkTo>
							</li>
						{{/each}}
					</nav>
				{{/if}}

				<div class="button-containers">

					<div
						class="generative-ai-container"
						{{didInsert this.addGenerativeAIButtonContainerReference}}
						{{willDestroy this.removeGenerativeAIButtonContainerReference}}
					></div>

					<div
						class="batch-button-container"
						{{didInsert this.addBatchButtonContainerReference}}
						{{willDestroy this.removeBatchButtonContainerReference}}
					></div>

					<div
						class="add-button-container"
						{{didInsert this.addAddButtonContainerReference}}
						{{willDestroy this.removeAddButtonContainerReference}}
					></div>
				</div>

			</div>
		</div>
	</template>
}
